/**
 * src/components/ForgotPasswordModal.tsx
 *
 * File-level:
 * - Reusable modal component used on the Login page for collecting a registered user code,
 *   email or mobile number for forgot-password requests.
 * - Visual update: header uses a bold gradient, backdrop uses a soft blurred gradient,
 *   and title text follows the requested wording.
 *
 * Notes:
 * - Accessible (role=dialog, aria-modal). Focus moves to the input when opened.
 * - The component is UI-only: it calls onSave and lets the parent decide what to do with the payload.
 * - TypeScript, small and self-contained.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface ForgotPayload {
  /** Type inferred from value: 'email' | 'mobile' | 'userCode' */
  type: 'email' | 'mobile' | 'userCode';
  /** Raw value entered by the admin/user */
  value: string;
  /** ISO timestamp for creation */
  createdAt: string;
}

/**
 * ForgotPasswordModalProps
 *
 * @interface Props for ForgotPasswordModal
 */
export interface ForgotPasswordModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called to close the modal */
  onClose: () => void;
  /**
   * Called when Save is clicked.
   * Receives the payload and may return a Promise (parent can persist to backend).
   */
  onSave?: (payload: ForgotPayload) => Promise<void> | void;
}

/**
 * inferType
 *
 * @description Small heuristic to guess whether the input is an email, mobile number
 * or a user code. This is purely for UX and does not block submission.
 *
 * @param v string
 * @returns 'email' | 'mobile' | 'userCode'
 */
function inferType(v: string): ForgotPayload['type'] {
  const s = v.trim();
  if (!s) return 'userCode';
  if (/@/.test(s) && /\.[a-z]{2,}$/i.test(s)) return 'email';
  const digits = s.replace(/[^0-9]/g, '');
  if (digits.length >= 6 && digits.length <= 16) return 'mobile';
  return 'userCode';
}

/**
 * ForgotPasswordModal
 *
 * @description Modal used on the Login page. Accepts user code OR email OR mobile no.
 * Visuals:
 * - Title: "Enter Registered User Code or Email Or Mobile No"
 * - Header: bold gradient (indigo -> pink)
 * - Backdrop: softened dark gradient with blur
 */
export default function ForgotPasswordModal({ open, onClose, onSave }: ForgotPasswordModalProps) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  /**
   * handleSave
   *
   * @description Validate non-empty input then call optional onSave.
   */
  async function handleSave() {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Please enter a User Code, Email or Mobile No.');
      inputRef.current?.focus();
      return;
    }
    const type = inferType(trimmed);
    const payload: ForgotPayload = { type, value: trimmed, createdAt: new Date().toISOString() };

    try {
      setSaving(true);
      if (onSave) {
        await onSave(payload);
      }
      // UX confirmation (client-side)
      toast.success('Received. We will notify soon.');
      // Close modal after small delay to let toast be noticed
      setTimeout(() => {
        setSaving(false);
        onClose();
      }, 300);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('ForgotPasswordModal save error', err);
      toast.error('Failed to submit request. Please try again.');
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enter Registered User Code or Email Or Mobile No"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
    >
      {/* Backdrop: softened dark gradient with blur */}
      <div
        onClick={() => {
          if (!saving) onClose();
        }}
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(12,18,35,0.72), rgba(35,21,60,0.64))',
          backdropFilter: 'blur(6px)',
        }}
      />

      {/* Modal Panel */}
      <div className="relative w-full max-w-lg rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10">
        {/* Header with bold gradient */}
        <div className="px-4 py-3 flex items-center justify-between gap-4 bg-gradient-to-r from-indigo-600 to-pink-500 text-white">
          <h2 className="text-lg font-semibold leading-tight">
            Enter Registered User Code or Email Or Mobile No
          </h2>
          <Button
            variant="ghost"
            className="bg-transparent text-white hover:bg-white/10"
            onClick={() => {
              if (!saving) onClose();
            }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="bg-white p-4 space-y-4">
          <p className="text-sm text-slate-600">
            Enter the registered User Code (e.g. RJBD240115P1S1), Email address or Mobile number.
            We will contact the registered address/number if found.
          </p>

          <div>
            <label htmlFor="forgot-input" className="block text-sm font-medium text-slate-700 mb-1">
              User Code / Email / Mobile No
            </label>
            <Input
              id="forgot-input"
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. RJBD240115P1S1 or Priya123@zmail.com or 9876xxxxxx"
              disabled={saving}
              aria-required
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              className="bg-transparent"
              onClick={() => {
                if (!saving) onClose();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-rose-600 hover:bg-rose-700 text-white" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}